import React, { useState, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";
import { MdZoomIn, MdZoomOut, MdCheck, MdClose } from "react-icons/md";
import { useTranslation } from "react-i18next";

interface ImageCropperProps {
  imageFile: File;
  onCrop: (croppedFile: File) => void;
  onCancel: () => void;
}

const createImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (err) => reject(err));
    img.setAttribute("crossOrigin", "anonymous");
    img.src = url;
  });
};

async function getCroppedImg(
  imageSrc: string,
  crop: { x: number; y: number; width: number; height: number },
  rotation = 0,
  fileName = "cropped.jpg"
): Promise<File> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No 2d context");

  // Set canvas size to crop size
  canvas.width = crop.width;
  canvas.height = crop.height;

  // Move the origin to the center of the canvas
  ctx.save();
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-canvas.width / 2, -canvas.height / 2);

  // Draw the image
  ctx.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    crop.width,
    crop.height
  );
  ctx.restore();

  return new Promise<File>((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(
            new File([blob], fileName, {
              type: "image/jpeg",
              lastModified: Date.now(),
            })
          );
        }
      },
      "image/jpeg",
      0.92
    );
  });
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  imageFile,
  onCrop,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const reader = new FileReader();
    reader.onload = (e) => setImageUrl(e.target?.result as string);
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  const onCropComplete = useCallback((_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCrop = useCallback(async () => {
    if (!croppedAreaPixels || !imageUrl) return;
    setLoading(true);
    try {
      const croppedFile = await getCroppedImg(
        imageUrl,
        croppedAreaPixels,
        0,
        imageFile.name
      );
      onCrop(croppedFile);
    } finally {
      setLoading(false);
    }
  }, [croppedAreaPixels, imageUrl, imageFile, onCrop]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("portal.profile.imageCropper.title")}
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <MdClose size={24} />
          </button>
        </div>
        {/* Cropper */}
        <div className="relative w-full h-[60vw] min-h-[220px] max-h-96 sm:h-96 bg-gray-100">
          {imageUrl && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={4 / 3}
              cropShape="rect"
              showGrid={true}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              minZoom={1}
              maxZoom={3}
              restrictPosition={false}
            />
          )}
        </div>
        {/* Controls */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between p-2 sm:p-4 border-t bg-gray-50 sticky bottom-0 z-10 w-full">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2 w-full sm:w-auto">
            <div className="flex flex-row items-center gap-2 w-full sm:w-auto">
              <button
                onClick={() => setZoom((z) => Math.max(1, z - 0.1))}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
                title={t("portal.profile.imageCropper.zoomOut")}
              >
                <MdZoomOut size={20} />
              </button>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="mx-2 w-full sm:w-32"
                ref={inputRef}
              />
              <button
                onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
                title={t("portal.profile.imageCropper.zoomIn")}
              >
                <MdZoomIn size={20} />
              </button>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto mt-2 sm:mt-0">
            <button
              onClick={onCancel}
              className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-xl font-medium shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors text-base"
            >
              <MdClose size={20} />
              {t("portal.profile.imageCropper.cancel")}
            </button>
            <button
              onClick={handleCrop}
              className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-semibold shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-400 transition-colors text-base disabled:opacity-60"
              disabled={loading}
            >
              {loading ? (
                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <MdCheck size={20} />
              )}
              {t("portal.profile.imageCropper.useImage")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
